import { useEffect } from 'react';

const getReducedMotion = () => (
  typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false
);

// Hero flight-path draw-on-scroll (stroke-dashoffset scrub + waypoint "lit" state).
function startHeroPath(root, reduceMotion) {
  const wrap = root.querySelector('#gw-hero-path');
  const draw = root.querySelector('#gw-path-draw');
  if (!wrap || !draw) return undefined;
  const glow = root.querySelector('#gw-path-glow');
  const items = Array.from(wrap.querySelectorAll('[data-stage]'));
  const len = draw.getTotalLength();
  [draw, glow].forEach((el) => {
    if (!el) return;
    el.style.strokeDasharray = len;
    el.style.strokeDashoffset = len;
  });

  const setLit = (li, lit) => {
    const dot = li.querySelector('[data-stage-dot]');
    const label = li.querySelector('[data-stage-label]');
    if (dot) {
      dot.style.opacity = lit ? '1' : '.62';
      dot.style.transform = lit ? 'scale(1)' : 'scale(.76)';
      dot.style.boxShadow = lit
        ? '0 6px 18px rgba(9,26,54,0.28), 0 0 0 6px rgba(140,198,255,0.22), 0 0 26px 6px rgba(80,160,255,0.4)'
        : '0 6px 18px rgba(9,26,54,0.22)';
    }
    if (label) label.style.opacity = lit ? '1' : '.58';
  };

  if (reduceMotion) {
    [draw, glow].forEach((el) => { if (el) el.style.strokeDashoffset = '0'; });
    items.forEach((li) => setLit(li, true));
    return undefined;
  }

  let queued = false;
  const apply = () => {
    queued = false;
    const vh = window.innerHeight || 900;
    const stack = root.querySelector('#gw-stack');
    const scrolled = stack ? -stack.getBoundingClientRect().top : (window.scrollY || 0);
    const p = Math.max(0, Math.min(1, scrolled / (vh * 0.62)));
    const eased = p * p * (3 - 2 * p);
    [draw, glow].forEach((el) => { if (el) el.style.strokeDashoffset = (len * (1 - eased)).toFixed(1); });
    items.forEach((li) => setLit(li, eased >= parseFloat(li.getAttribute('data-stage')) - 0.02));
  };
  const onScroll = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(apply);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  apply();
  return () => {
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onScroll);
  };
}

// Parallax: hero photo pin-drift + generic [data-parallax] nodes.
function startParallax(root, reduceMotion) {
  if (reduceMotion) return undefined;
  const nodes = Array.from(root.querySelectorAll('[data-parallax]'));
  if (!nodes.length) return undefined;

  nodes.forEach((n) => {
    n.style.willChange = 'transform';
    if (n.id === 'gw-hero') {
      n.style.height = '112%';
      n.style.top = '-11%';
      n.style.position = 'absolute';
      n.style.left = '0';
      n.style.width = '100%';
    }
  });

  let queued = false;
  const apply = () => {
    queued = false;
    const vh = window.innerHeight || 900;
    if (window.innerWidth < 860) {
      nodes.forEach((n) => { n.style.transform = 'none'; });
      return;
    }
    const stack = root.querySelector('#gw-stack');
    nodes.forEach((n) => {
      const factor = parseFloat(n.getAttribute('data-parallax')) || 0;
      if (n.id === 'gw-hero' && stack) {
        const sr = stack.getBoundingClientRect();
        const range = Math.max(1, sr.height - vh);
        const p = Math.max(0, Math.min(1, -sr.top / range));
        n.style.transform = `translate3d(0,${(p * -90).toFixed(1)}px,0)`;
        return;
      }
      const r = n.getBoundingClientRect();
      if (r.bottom < -200 || r.top > vh + 200) return;
      const offset = ((r.top + r.height / 2) - vh / 2) * factor;
      n.style.transform = `translate3d(0,${offset.toFixed(1)}px,0)`;
    });
  };
  const onScroll = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(apply);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  apply();
  return () => {
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onScroll);
  };
}

// Journey section: scroll-scrubbed plane + progress line.
function startScrub(root) {
  const panel = root.querySelector('#gw-journey-panel');
  const plane = root.querySelector('#gw-plane');
  if (!panel || !plane) return undefined;
  const line = root.querySelector('#gw-line-progress');
  const rings = Array.from(root.querySelectorAll('#gw-journey-svg [data-ring]'));
  const dots = Array.from(root.querySelectorAll('#gw-journey-svg [data-dot]'));
  const SPAN = 1060;
  const stopOf = (el) => (parseFloat(el.getAttribute('cx')) - 70) / SPAN;

  let queued = false;
  const apply = () => {
    queued = false;
    const r = panel.getBoundingClientRect();
    const vh = window.innerHeight || 900;
    const start = vh * 0.78;
    const end = vh * 0.32;
    const raw = (start - r.top) / Math.max(1, (r.height + start - end));
    const p = Math.max(0, Math.min(1, raw));
    plane.setAttribute('transform', `translate(${(p * SPAN).toFixed(1)} 0)`);
    if (line) line.setAttribute('stroke-dashoffset', (SPAN * (1 - p)).toFixed(1));
    rings.forEach((el) => {
      const lit = p >= stopOf(el) - 0.012;
      el.setAttribute('opacity', lit ? '0.95' : '0.28');
      el.setAttribute('stroke-width', lit ? '2.6' : '2');
    });
    dots.forEach((el) => {
      const lit = p >= stopOf(el) - 0.012;
      el.setAttribute('opacity', lit ? '1' : '0.5');
      el.setAttribute('r', lit ? '7.5' : '5');
    });
  };
  const onScroll = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(apply);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  apply();
  return () => {
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onScroll);
  };
}

// Scroll reveals: fade/translate-in on first intersection, staggered by sibling index.
function startReveals(root, reduceMotion) {
  if (reduceMotion || typeof IntersectionObserver === 'undefined') return undefined;
  const nodes = root.querySelectorAll('[data-reveal]');
  if (!nodes.length) return undefined;
  const vh = window.innerHeight || 900;
  const io = new IntersectionObserver((entries) => {
    entries.forEach((en) => {
      if (!en.isIntersecting) return;
      io.unobserve(en.target);
      const peers = en.target.parentElement
        ? Array.from(en.target.parentElement.querySelectorAll(':scope > [data-reveal]'))
        : [];
      const idx = Math.max(0, peers.indexOf(en.target));
      en.target.style.transitionDelay = `${idx * 90}ms`;
      en.target.style.opacity = '1';
      en.target.style.transform = 'none';
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

  nodes.forEach((n) => {
    const r = n.getBoundingClientRect();
    if (r.top < vh * 0.9) return; // already in view on load — leave it painted
    n.style.opacity = '0';
    n.style.transform = 'translateY(22px)';
    n.style.transition = 'opacity .7s cubic-bezier(.2,.8,.2,1), transform .7s cubic-bezier(.2,.8,.2,1)';
    n.style.willChange = 'opacity, transform';
    io.observe(n);
  });

  return () => io.disconnect();
}

// Animated stat counters.
function startTickers(root, reduceMotion) {
  const nodes = root.querySelectorAll('#gw-ticker [data-ticker]');
  if (!nodes.length || reduceMotion || typeof IntersectionObserver === 'undefined') return undefined;
  const fmt = new Intl.NumberFormat('en-GB');
  const run = (el) => {
    const target = parseInt(el.getAttribute('data-ticker'), 10);
    if (!target) return;
    const dur = 1600;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min((t - t0) / dur, 1);
      const eased = 1 - (1 - p) ** 3;
      el.textContent = fmt.format(Math.round(target * eased));
      if (p < 1) requestAnimationFrame(tick);
    };
    el.textContent = fmt.format(0);
    requestAnimationFrame(tick);
  };
  const io = new IntersectionObserver((entries) => {
    entries.forEach((en) => {
      if (!en.isIntersecting) return;
      io.unobserve(en.target);
      run(en.target);
    });
  }, { threshold: 0.4 });
  nodes.forEach((n) => io.observe(n));
  return () => io.disconnect();
}

// Wires up all of the home page's scroll-driven behaviour, scoped to the
// page's own DOM subtree (pageRef) rather than global document IDs.
export default function useHomeInteractions(pageRef) {
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return undefined;
    const reduceMotion = getReducedMotion();

    const cleanups = [
      startHeroPath(root, reduceMotion),
      startParallax(root, reduceMotion),
      startScrub(root),
      startReveals(root, reduceMotion),
      startTickers(root, reduceMotion),
    ].filter(Boolean);

    return () => cleanups.forEach((fn) => fn());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
